import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { MapPin, Loader2, Target, Lock } from "lucide-react";

const fetchLocal = () => api.get("/admin/local-gravity").then((r) => r.data);

const CELL = {
  DOMINATING: "bg-green-500 text-white",
  STRONG: "bg-emerald-400 text-white",
  DEVELOPING: "bg-amber-300 text-amber-900",
  WEAK: "bg-orange-300 text-orange-900",
  "CRITICAL GAP": "bg-red-400 text-white",
};

export default function LocalGravity() {
  const { data, isLoading } = useQuery({ queryKey: ["local-gravity"], queryFn: fetchLocal });
  if (isLoading || !data) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const { topics, cities, summary, top_gaps } = data;

  return (
    <div data-testid="local-gravity-root">
      <div>
        <h1 className="text-2xl font-bold text-navy flex items-center gap-2"><MapPin className="h-6 w-6 text-launch" /> Local Topical Gravity</h1>
        <p className="text-slate-500 mt-1 max-w-3xl">Which HVAC topics you own <strong>city-by-city</strong> across Brevard. Templated service+city pages give baseline presence; real ownership comes from unique local content and reviews. Green = owned, red = gap.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        {[["Local Topics", summary.topics], ["Cities", summary.cities], ["Avg Local Gravity", summary.avg_local_gravity], ["Gap Cells", summary.gap_cells]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-2xl font-bold text-navy">{v}</div><div className="text-xs font-semibold text-slate-600 mt-0.5">{l}</div></div>
        ))}
      </div>

      {/* Heatmap */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="local-gravity-table">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3 sticky left-0 bg-slate-50 z-10">Topic</th>
                {cities.map((c) => <th key={c.slug} className="px-2 py-3 text-center whitespace-nowrap">{c.city}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {topics.map((t) => (
                <tr key={t.slug} data-testid={`local-row-${t.slug}`}>
                  <td className="px-3 py-2 font-semibold text-navy sticky left-0 bg-white z-10 whitespace-nowrap">{t.name}</td>
                  {t.cells.map((cell) => (
                    <td key={cell.city_slug} className="px-1.5 py-1.5 text-center">
                      <div className={`mx-auto flex h-9 w-12 flex-col items-center justify-center rounded ${CELL[cell.status]}`} title={`${cell.status} · ${cell.local_blog_count} local posts · ${cell.reviews} reviews`}>
                        <span className="text-xs font-bold leading-none">{cell.score}</span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top local gaps */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="local-top-gaps">
        <h2 className="text-lg font-bold text-navy flex items-center gap-2"><Target className="h-5 w-5 text-launch" /> Biggest Local Gaps to Close</h2>
        <p className="text-xs text-slate-500 mb-3">Highest-value topic + city combinations where you can build local authority next.</p>
        {top_gaps.length ? (
          <div className="grid sm:grid-cols-2 gap-2">
            {top_gaps.map((g, i) => (
              <div key={`${g.slug}-${g.city_slug}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
                <div><span className="text-xs text-slate-400 mr-1">#{i + 1}</span><span className="font-semibold text-navy">{g.topic}</span> <span className="text-slate-500">· {g.city}</span>
                  <div className="text-[12px] text-slate-500">Publish unique {g.city} content + gather local reviews</div>
                </div>
                <span className="shrink-0 text-right"><span className="text-sm font-bold text-launch">{g.opportunity}</span><span className="block text-[10px] text-slate-400">score {g.score}</span></span>
              </div>
            ))}
          </div>
        ) : <p className="text-slate-400 text-sm">No local gaps detected.</p>}
      </div>

      <p className="mt-4 text-[11px] text-slate-400 flex items-center gap-1"><Lock className="h-3 w-3" /> Local Google rankings & map-pack positions require a connected source (GBP / SerpApi) — not shown here.</p>
    </div>
  );
}
